import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { StateService } from '@uirouter/core';
import { combineLatest, of } from 'rxjs';
import {
    catchError,
    map,
    startWith,
    switchMap,
    take,
    tap
} from 'rxjs/operators';
import * as fromShared from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromRoot from '../../store';
import { UserRoleEnum } from '../../user-info';
import * as fromUserInfo from '../../user-info/store';
import * as fromUserRole from '../../user-role/store';
import { IUserInfo } from '../../user-info/IUserInfo';
import { IUserAccess } from '../../user-info/store/user.reducer';
import { RegistrationPrefetchService } from '../../data-services/registration';
import { AuthActionTypes, LoginSuccess, Logout } from './auth.actions';
@Injectable()
export class AuthenticationEffects {
    constructor(
        private log: LoggingService,
        private actions$: Actions,
        private ss: StateService,
        private store: Store<fromRoot.State>,
        private registrationPrefetch: RegistrationPrefetchService
    ) {}
    Login$ = createEffect(
        () =>
            this.actions$
                .pipe(ofType<LoginSuccess>(AuthActionTypes.LoginSuccess))
                .pipe(
                    switchMap(login =>
                        combineLatest([
                            this.actions$.pipe(
                                ofType<fromUserInfo.SetUserInfo>(
                                    fromUserInfo.UserActionTypes.SetUserInfo
                                )
                            ),
                            this.actions$.pipe(
                                ofType<fromUserInfo.SetUserPermissions>(
                                    fromUserInfo.UserActionTypes
                                        .SetUserPermissions
                                )
                            ),
                            this.actions$.pipe(
                                ofType<fromUserRole.SetUserRole>(
                                    fromUserRole.UserRoleActionTypes.SetUserRole
                                )
                            ),
                            this.store.pipe(
                                select(fromRoot.getRedirect),
                                startWith('')
                            )
                        ]).pipe(
                            take(1),
                            switchMap(([userInfo, permissions, roles, redir]) => {
                                const isAdmin = roles.payload.some(
                                    x =>
                                        x.RoleGroupID ===
                                        UserRoleEnum.Administrator
                                );
                                const ensureRegistration$ = isAdmin
                                    ? this.registrationPrefetch.ensureLoaded$()
                                    : of(null);

                                return ensureRegistration$.pipe(
                                    tap(() => {
                                        this.log.verbose({
                                            type: 'AuthenticationEffects:Login$',
                                            permissions,
                                            userInfo,
                                            isAdmin
                                        });

                                        const initialRouteName =
                                            fromShared.normalizeInitialRouteName(
                                                userInfo.payload.Initialroutename
                                            );

                                        if (initialRouteName) {
                                            this.navigateToInitialRoute(
                                                userInfo.payload,
                                                permissions.payload,
                                                redir,
                                                initialRouteName
                                            );
                                            return;
                                        }

                                        if (isAdmin) {
                                            this.log.verbose({
                                                type: 'SetUserGroups$',
                                                message:
                                                    'user is admin, redirecting to register (auth.effects)'
                                            });
                                            return;
                                        }

                                        this.ss.go(
                                            redir ? redir : 'dashboard',
                                            {},
                                            { reload: true }
                                        );
                                    })
                                );
                            }),
                            catchError(fromShared.Errors.handleServiceError)
                        )
                    )
                ),
        { dispatch: false }
    );
    Logout$ = createEffect(() =>
        this.actions$.pipe(
            ofType<Logout>(AuthActionTypes.Logout),
            tap(() =>
                this.ss.go(
                    'main',
                    {},
                    {
                        reload: true,
                        location: 'replace',
                        source: 'redirect',
                        inherit: false
                    }
                )
            ),
            map(() => new fromShared.Errors.SetErrors([]))
        )
    );

    private navigateToInitialRoute(
        userInfo: IUserInfo,
        permissions: IUserAccess,
        redir: string,
        initialRouteName: string
    ): void {
        let initialProcess = fromShared.resolveSystemRouteProcess(
            initialRouteName
        );

        if (!initialProcess) {
            permissions.UserProcessCategories.forEach(category => {
                category.UserProcesses.forEach(process => {
                    if (
                        initialRouteName ===
                        process.RouteName?.trim().toLowerCase()
                    ) {
                        initialProcess = process;
                    }
                });
            });
        }

        if (initialProcess) {
            this.store.dispatch(
                fromShared.upsertProcess({
                    process: initialProcess
                })
            );
        }

        const destinationState =
            fromShared.resolveInitialRouteStateName(initialRouteName) ??
            initialRouteName;

        this.log.verbose({
            type: 'AuthenticationEffects:Login$:navigateToInitialRoute',
            initialRouteName,
            destinationState,
            initialProcess
        });

        this.ss.go(
            redir ? redir : destinationState,
            {
                process: {
                    ...(initialProcess ?? {}),
                    catId: userInfo.Initialcatid
                }
            },
            { reload: true }
        );
    }
}
